import { Component, EventEmitter, Input, Output, inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import {
  ReactiveFormsModule,
  FormGroup,
  FormControl,
  Validators,
  AbstractControl,
  FormBuilder,
  FormsModule
} from '@angular/forms';
import { AlertService } from '../../../_services/alert.service';
import { Customer } from '../../../_interfaces/customer';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { AsyncPipe, formatDate } from '@angular/common';
import { CouponService } from '../../../_services/coupon.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TypeheadComponent } from '../../../../Shared/_components/typehead/typehead.component';
import { NgClass } from '@angular/common';
import { MultiSelectTypeheadComponent } from '../../../../Shared/_components/multi-select-typehead/multi-select-typehead.component';
import { UserService } from '../../../_services/users.service';
import { userRoles } from '../../../../Shared/_constants/constants';
import { ProductService } from '../../../_services/product.service';
import { CategoryService } from '../../../_services/category.service';
import { Category } from '../../../_interfaces/category';
import { MatIconModule } from '@angular/material/icon';

// A percentage can't go over 100; a flat amount just can't be negative.
function couponValueValidator(control: AbstractControl) {
  if (control.value === null || control.value === '') return { required: true };
  const value = Number(control.value);
  if (value < 0) return { min: true };
  if (value > 100 && control.parent?.get('couponValuetype')?.value === 'PERC') return { maxPerc: true };
  return null;
}

@Component({
    selector: 'app-add-edit-coupons',
    imports: [
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    AsyncPipe,
    MatTooltipModule,
    TypeheadComponent,
    NgClass,
    MultiSelectTypeheadComponent,
    FormsModule,
    MatIconModule
],
    templateUrl: './add-edit-coupons.component.html',
    styleUrl: './add-edit-coupons.component.css'
})
export class AddEditCouponsComponent implements OnInit {
  // Works as a dialog (sales screens, quick edits) or inside the coupon edit
  // page: there it gets the coupon through `pageData` and reports back through
  // `done` instead of closing a dialog.
  readonly dialogRef = inject(MatDialogRef<AddEditCouponsComponent>, { optional: true });
  private readonly dialogData = inject<any>(MAT_DIALOG_DATA, { optional: true });
  @Input() pageData: { type: 'ADD' | 'EDIT'; cuopon?: any } | null = null;
  @Output() done = new EventEmitter<{ status: boolean }>();

  get data(): any {
    return this.pageData ?? this.dialogData ?? { type: 'ADD' };
  }

  get pageMode(): boolean {
    return !this.dialogRef;
  }

  // Unsaved edits on the form (the page asks before leaving with them).
  get hasChanges(): boolean {
    return this.couponCodeAddEditFrom.dirty;
  }

  finish(result: { status: boolean }): void {
    if (this.dialogRef) this.dialogRef.close(result);
    else this.done.emit(result);
  }
  customerName = new FormControl('');
  selectedCust: number = 0;
  selectedCategoriesInclude: any[] = [];
  selectedCategoriesExclude: any[] = [];
  selectedProductsInclude: any[] = [];
  selectedProductsExclude: any[] = [];
  customers: Customer[] = [];
  products: any[] = [];
  categories: any[] = [];
  customerFullName: string = '';
  activeTab = 1;
  today = formatDate(new Date(), 'yyyy-MM-dd', 'en');
  selectedCustomers: any[] = [];
  noExpiry: boolean = false;
  giveAwayEnabled: boolean = false;
  giveAwayType: string = 'POPUP_1_PROD_SELECTION';
  giveAwaySingleProdSelectionProds: any[] = [];
  productSuggestionList: {
    id: number;
    name: string;
    slug?: string;
    sku: string;
  }[] = [];
  giveAwayProductLoadingInProgress = false;
  giveAwayCurrentProductSelection: any;
  giveAwayCurrentVariantSelection: string[] = [];
  couponValidUserRoles: string[] = [];
  selectedUserRoleToAdd: string = 'ALL';
  giveAwayCouponsList: any[] = [];
  selectedAssociatedCodes: number[] = [];
  readonly roleOptions = ['BRONZE', 'SILVER', 'GOLD', 'PLATINUM', 'NONMEMBER'];
  saving = false;

  // Editing an existing code, or starting a new one from a copy of a row.
  get isEdit(): boolean {
    return this.data.type === 'EDIT';
  }
  get isCopy(): boolean {
    return this.data.type === 'ADD' && !!this.data.cuopon;
  }

  couponCodeAddEditFrom: FormGroup = new FormGroup({
    couponCode: new FormControl(''),
    couponDesc: new FormControl(''),
    couponValue: new FormControl(0),
    couponValuetype: new FormControl(),
    useTimes: new FormControl(0),
    expiryDate: new FormControl(''),
  });

  constructor(
    private formBuilder: FormBuilder,
    private alertService: AlertService,
    private couponService: CouponService,
    private categoryService: CategoryService,
    private productService: ProductService,
    private userService: UserService,
  ) {}

  // The four lists below (customers, categories, products, give-away
  // coupons) used to be awaited together before the form was even built, so
  // the dialog sat empty until the slowest one - the full products list -
  // came back. Now the form builds immediately from the row being edited,
  // and each list fills in as it arrives (selections that depend on a list
  // are applied then, see applyEditSelections).
  loadingOptions = 0;

  private beginLoad() {
    this.loadingOptions++;
  }

  private endLoad() {
    this.loadingOptions = Math.max(0, this.loadingOptions - 1);
    this.applyEditSelections();
  }

  private loadOptionLists() {
    this.beginLoad();
    this.userService.getCustomers(userRoles.CUSTOMER).subscribe({
      next: (response) => {
        this.customers = response;
        this.endLoad();
      },
      error: (error) => {
        this.alertService.alert(error.error?.message || error.message, 'error');
        this.endLoad();
      },
    });

    this.beginLoad();
    this.categoryService.getAllCategories().subscribe({
      next: (response: any) => {
        this.categories = response.flatMap((cat: Category) => [
          cat,
          ...(cat.subCategories || []).map((subCat: Category) => subCat),
        ]);
        this.endLoad();
      },
      error: (error) => {
        this.alertService.alert(error.error?.message || error.message, 'error');
        this.endLoad();
      },
    });

    this.beginLoad();
    this.productService.getAllProducts().subscribe({
      next: (response: any) => {
        this.products = response;
        this.productSuggestionList = this.products.map((p: any) => ({
          id: p.productId,
          name: p.productName,
          slug: p.productSlug,
          sku: p.productvariants?.[0]?.sku,
        }));
        this.endLoad();
      },
      error: (error) => {
        this.alertService.alert(error.error?.message || error.message, 'error');
        this.endLoad();
      },
    });

    // Only the give-away coupons are needed here, and none of their
    // category/product rules.
    this.beginLoad();
    this.couponService
      .getAllCoupons({ pageIndex: 0, pageSize: 1000, keywordSearch: '', giveAwayOnly: true, lite: true })
      .subscribe({
        next: (response: any) => {
          const editingId = this.data.type === 'EDIT' && this.data.cuopon ? this.data.cuopon.id : null;
          this.giveAwayCouponsList = (response.couponCodes || []).filter(
            (c: any) => c.giveAway !== null && c.id !== editingId,
          );
          this.endLoad();
        },
        error: () => this.endLoad(),
      });
  }

  // Edit mode: turns the row's saved ids into the selected objects the
  // pickers need. Runs after every list arrives (and once up front), and is
  // safe to repeat - each block only acts once its list has data.
  private applyEditSelections() {
    if (!this.data.cuopon) return;
    const coupon = this.data.cuopon;

    // Name and id come straight off the row, so they're right even before
    // the customers list arrives (submit relies on selectedCust).
    if (coupon.customer) {
      this.customerFullName = `${coupon.customer.firstname} ${coupon.customer.lastname}`;
      this.selectedCust = coupon.customer.userid;
      if (this.customers.length) {
        this.selectedCustomers = this.customers.filter((cus: any) => this.selectedCust === cus.userid);
      }
    }

    if (coupon.couponCategories && this.categories.length) {
      const idsInclude = coupon.couponCategories.filter((c: any) => c.include == true).map((c: any) => c.categoryId);
      this.selectedCategoriesInclude = this.categories.filter((cat: any) => idsInclude.includes(cat.categoryId));
      const idsExclude = coupon.couponCategories.filter((c: any) => c.include == false).map((c: any) => c.categoryId);
      this.selectedCategoriesExclude = this.categories.filter((cat: any) => idsExclude.includes(cat.categoryId));
    }

    if (coupon.couponProducts && this.products.length) {
      const idsInclude = coupon.couponProducts.filter((c: any) => c.include == true).map((c: any) => c.productId);
      this.selectedProductsInclude = this.products.filter((p: any) => idsInclude.includes(p.productId));
      const idsExclude = coupon.couponProducts.filter((c: any) => c.include == false).map((c: any) => c.productId);
      this.selectedProductsExclude = this.products.filter((p: any) => idsExclude.includes(p.productId));
    }

    if (coupon.subCouponCodes && this.giveAwayCouponsList.length) {
      const codes = String(coupon.subCouponCodes).split(',').map((s) => s.trim());
      this.selectedAssociatedCodes = this.giveAwayCouponsList
        .filter((c) => codes.includes(c.couponCode))
        .map((c) => c.id);
    }
  }

  async ngOnInit(): Promise<void> {
    this.loadOptionLists();
   // this.getAllCategories();
   // this.getAllProducts();

    this.couponCodeAddEditFrom = this.formBuilder.group({
      couponCode: new FormControl('', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(100),
      ]),
      couponDesc: new FormControl('', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(200),
      ]),
      couponValue: new FormControl(0, [couponValueValidator]),
      couponValuetype: new FormControl('FLAT', [Validators.required]),
      useTimes: new FormControl(1, [Validators.required, Validators.min(0)]),
      useTimesCustomer: new FormControl(1, [
        Validators.required,
        Validators.min(0),
      ]),
      // A new code lasts a month unless told otherwise (it used to expire the day it was made).
      expiryDate: new FormControl(formatDate(new Date(Date.now() + 30 * 24 * 3600 * 1000), 'yyyy-MM-dd', 'en')),
      minSpend: new FormControl(0, [Validators.required, Validators.min(0)]),
      maxSpend: new FormControl(0, [Validators.required, Validators.min(0)]),
      priceRangeApplyTo: new FormControl('ORDER_VALUE', [Validators.required]),
      individualUse: new FormControl(false),
      excludeSales: new FormControl(false),
      invalidateTradeDiscounts: new FormControl(false),
      internalUseOnly: new FormControl(false),
      selectedUserRoleToAdd: new FormControl('ALL')
    });
    // The value's limit depends on the type (a percentage stops at 100).
    this.couponCodeAddEditFrom.get('couponValuetype')?.valueChanges.subscribe(() =>
      this.couponCodeAddEditFrom.get('couponValue')?.updateValueAndValidity(),
    );
    if (this.data.cuopon) {
      this.noExpiry = this.data.cuopon.expiryDate ? false : true;
      this.couponCodeAddEditFrom.setValue({
        // A copy starts without a code: it has to be unique.
        couponCode: this.isCopy ? '' : this.data.cuopon.couponCode,
        couponDesc: this.data.cuopon.couponDesc,
        couponValue: this.data.cuopon.couponValue,
        couponValuetype: this.data.cuopon.couponPerc ? 'PERC' : 'FLAT',
        useTimes: this.data.cuopon.useTimes ? this.data.cuopon.useTimes : 0,
        useTimesCustomer: this.data.cuopon.useTimesCustomer
          ? this.data.cuopon.useTimesCustomer
          : 0,
        minSpend: this.data.cuopon.minSpend ? this.data.cuopon.minSpend : 0,
        maxSpend: this.data.cuopon.maxSpend ? this.data.cuopon.maxSpend : 0,
        expiryDate: this.startingExpiry(),
        individualUse: this.data.cuopon.individualUse,
        excludeSales: this.data.cuopon.excludeSales,
        priceRangeApplyTo: this.data.cuopon.priceRangeApplyTo,
        invalidateTradeDiscounts: this.data.cuopon.invalidateTradeDiscounts == 1 ? true : false,
        internalUseOnly: this.data.cuopon.internalUseOnly == 1 ? true : false,
        selectedUserRoleToAdd: 'ALL'
      });

      if (this.data.cuopon.userRoles && this.data.cuopon.userRoles !== '') {
        this.couponValidUserRoles = JSON.parse(this.data.cuopon.userRoles);
      }

      if (this.data.cuopon.giveAway && this.data.cuopon.giveAway !== '') {
        const giveAwayData = JSON.parse(this.data.cuopon.giveAway);
        this.giveAwayEnabled = true;
        // legacy value stored as POPUP_SINGLE_PROD_SELECTION maps to POPUP_1_PROD_SELECTION
        this.giveAwayType = giveAwayData.type === 'POPUP_SINGLE_PROD_SELECTION'
          ? 'POPUP_1_PROD_SELECTION'
          : giveAwayData.type;
        if (giveAwayData.data?.products?.length > 0) {
          this.giveAwaySingleProdSelectionProds = giveAwayData.data.products;
        }
      }
      this.applyEditSelections();
    }
  }

  private async getAllCategories() {
    await this.categoryService.getAllCategories().subscribe({
      next: (response: any) => {
        if (response) {
          this.categories = response.flatMap((cat: Category) => [
            cat,
            ...(cat.subCategories || []).map((subCat: Category) => (subCat)),
          ]);
        }
      },
      error: (error: { error: { message: any } }) =>
        this.alertService.alert(error.error.message, 'error'),
    });
  }

  private async getAllProducts() {
      await this.productService.getAllProducts().subscribe({
        next: (response) => {
          this.products = response;
           this.productSuggestionList = this.products.map((p: any) => {
            return {
              id: p.productId,
              name: p.productName,
              slug: p.productSlug,
              sku: p.productvariants[0].sku,
            };
          });
        },
        error: (error) => this.alertService.alert(error.error.message, 'error'),
      });
  }

  private async getAllCustomers() {
    await this.userService.getCustomers( userRoles.CUSTOMER).subscribe({
      next: (response) => {
        this.customers = response;
      },
      error: (error) => this.alertService.alert(error.error.message, 'error'),
    });
  }
  // The row's expiry, or - for a copy of a code that has already lapsed - a
  // month from today.
  private startingExpiry(): string {
    const expiry = this.data.cuopon?.expiryDate;
    if (!expiry) return formatDate(new Date(), 'yyyy-MM-dd', 'en');
    const day = formatDate(expiry, 'yyyy-MM-dd', 'en');
    if (this.isCopy && day < this.today) {
      return formatDate(new Date(Date.now() + 30 * 24 * 3600 * 1000), 'yyyy-MM-dd', 'en');
    }
    return day;
  }

  generateCode() {
    const letters = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = '';
    for (let i = 0; i < 8; i++) code += letters[Math.floor(Math.random() * letters.length)];
    this.couponCodeAddEditFrom.patchValue({ couponCode: code });
    this.couponCodeAddEditFrom.get('couponCode')?.markAsDirty();
  }

  // The plain-English line at the top of the dialog, kept up to date as the
  // form changes.
  get summary(): string[] {
    const v = this.couponCodeAddEditFrom.value;
    const out: string[] = [];
    const value = Number(v.couponValue) || 0;
    out.push(`${v.couponValuetype === 'PERC' ? value + '%' : '£' + value} off`);
    if (Number(v.minSpend) > 0) out.push(`min spend £${v.minSpend}`);
    if (Number(v.maxSpend) > 0) out.push(`max spend £${v.maxSpend}`);
    out.push(Number(v.useTimes) > 0 ? `${v.useTimes} use${Number(v.useTimes) === 1 ? '' : 's'} in total` : 'unlimited uses');
    if (Number(v.useTimesCustomer) > 0) out.push(`${v.useTimesCustomer} per customer`);
    out.push(this.noExpiry || !v.expiryDate ? 'no expiry' : `expires ${formatDate(v.expiryDate, 'd MMM y', 'en')}`);
    return out;
  }

  // Maximum spend below the minimum could never apply.
  get spendRangeInvalid(): boolean {
    const min = Number(this.couponCodeAddEditFrom.value.minSpend) || 0;
    const max = Number(this.couponCodeAddEditFrom.value.maxSpend) || 0;
    return max > 0 && max < min;
  }

  // A dot on a tab when something inside it needs attention.
  tabHasError(tab: number): boolean {
    const names: Record<number, string[]> = {
      1: ['couponCode', 'couponDesc', 'couponValue', 'couponValuetype', 'expiryDate'],
      2: ['minSpend', 'maxSpend', 'useTimes', 'useTimesCustomer', 'priceRangeApplyTo'],
    };
    const bad = (names[tab] ?? []).some((n) => {
      const c = this.couponCodeAddEditFrom.get(n);
      return c && c.invalid && (c.dirty || c.touched);
    });
    return bad || (tab === 2 && this.spendRangeInvalid);
  }

  get f(): { [key: string]: AbstractControl } {
    return this.couponCodeAddEditFrom.controls;
  }

  async onSubmit(e: any) {
    e.preventDefault();
    if (this.saving) return;
    // Saving an edit before the category/product lists have loaded would
    // send empty selections and wipe the coupon's saved rules.
    if (this.loadingOptions > 0) {
      this.alertService.alert('Still loading options - try again in a moment', 'error');
      return;
    }
    if (this.couponCodeAddEditFrom.invalid || this.spendRangeInvalid) {
      this.couponCodeAddEditFrom.markAllAsTouched();
      this.alertService.alert('Please provide valid information', 'error');
      return;
    }

    const formData = this.couponCodeAddEditFrom.value;

    let couponCategoriesInclude = this.selectedCategoriesInclude.map((c) => {
      return { categoryId: c.categoryId, include: true };
    });
    let couponCategoriesExclude = this.selectedCategoriesExclude.map((c) => {
      return { categoryId: c.categoryId, include: false };
    });

    let couponProductsInclude = this.selectedProductsInclude.map((c) => {
      return { productId: c.productId, include: true };
    });
    let couponProductsExclude = this.selectedProductsExclude.map((c) => {
      return { productId: c.productId, include: false };
    });
    const inputParams: any = {
      couponCode: formData.couponCode,
      couponDesc: formData.couponDesc,
      couponValue: formData.couponValue,
      couponPerc: formData.couponValuetype === 'PERC',
      customerId: this.selectedCust,
      useTimes: formData.useTimes,
      expiryDate: this.noExpiry ? null : formData.expiryDate,
      minSpend: formData.minSpend,
      maxSpend: formData.maxSpend,
      individualUse: formData.individualUse,
      excludeSales: formData.excludeSales,
      useTimesCustomer: formData.useTimesCustomer,
      priceRangeApplyTo: formData.priceRangeApplyTo,
      invalidateTradeDiscounts: formData.invalidateTradeDiscounts,
      internalUseOnly: formData.internalUseOnly,
      couponCategories: couponCategoriesInclude.concat(couponCategoriesExclude),
      couponProducts: couponProductsInclude.concat(couponProductsExclude),
      userRoles: this.couponValidUserRoles.length > 0 ? JSON.stringify(this.couponValidUserRoles) : null,
      subCouponCodes: this.selectedAssociatedCodes.length > 0
        ? this.giveAwayCouponsList
            .filter(c => this.selectedAssociatedCodes.includes(c.id))
            .map(c => c.couponCode)
            .join(',')
        : null
    };

    if (this.giveAwayEnabled && this.giveAwaySingleProdSelectionProds.length > 0) {
      const giveAway = {
        type: this.giveAwayType,
        data: {
          products: this.giveAwaySingleProdSelectionProds.map((p) => ({
            productName: p.productName,
            productSlug: p.productSlug,
            variantSku: p.variantSku,
          })),
        },
      };
      inputParams.giveAway = JSON.stringify(giveAway);
    }

    if (this.isEdit) {
      inputParams.id = this.data.cuopon.id;
    }

    this.saving = true;
    this.couponService
      .createUpdateCoupon(this.data.type, inputParams)
      .subscribe({
        next: (response) => {
          const successMsg = `Coupon code ${
            this.data.type === 'ADD' ? 'created' : 'updated'
          } successfully!`;
          this.alertService.alert(successMsg, 'success');
          this.saving = false;
          this.couponCodeAddEditFrom.markAsPristine();
          this.finish({ status: true });
        },
        // Stay open on a failure (e.g. the code already exists) so nothing typed is lost.
        error: (error) => {
          this.saving = false;
          this.alertService.alert(error.error?.message || 'Could not save the coupon', 'error');
        },
      });
  }

  onCustomerSelection(selectedCust: any) {
    // Clearing the customer makes the code open to everyone again.
    this.selectedCust = selectedCust && selectedCust[0] ? selectedCust[0].userid : 0;
    // right now we need only single custoemr, but later we will need multi customer selection
  }

  onProductIncludeSelection(selectedProducts: any) {
    this.selectedProductsInclude = selectedProducts;
  }

  onProductExcludeSelection(selectedProducts: any) {
    this.selectedProductsExclude = selectedProducts;
  }

  onCategoryIncludeSelection(selectedCategory: any) {
    this.selectedCategoriesInclude = selectedCategory;
  }

  onCategoryExcludeSelection(selectedCategory: any) {
    this.selectedCategoriesExclude = selectedCategory;
  }

  onGiveAwayTypeChanged(giveAwayType: string) {
    this.giveAwayType = giveAwayType;
  }

  onMotherProductSelection(selectedProduct: any) {
    const prod = this.products.filter((p: any) => p.productId == selectedProduct.id);
    if (prod && prod.length > 0) {
      this.giveAwayCurrentProductSelection = prod[0];
      this.giveAwayCurrentVariantSelection = [];
    }
  }

  onVariantCheckChange(sku: string, event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    if (checked) {
      if (!this.giveAwayCurrentVariantSelection.includes(sku)) {
        this.giveAwayCurrentVariantSelection.push(sku);
      }
    } else {
      this.giveAwayCurrentVariantSelection = this.giveAwayCurrentVariantSelection.filter(s => s !== sku);
    }
  }

  onAddUserRole() {
    const selecteduseRole = this.couponCodeAddEditFrom.get('selectedUserRoleToAdd')?.value;
    if(selecteduseRole !== 'ALL') {
      if(!this.couponValidUserRoles.includes(selecteduseRole)) {
        this.couponValidUserRoles.push(selecteduseRole);
      }
    }
  }

  onDeleteUserRoleFromList(index:number) {
    this.couponValidUserRoles.splice(index, 1);
  }

  addProductToGiveAwayList() {
    if (!this.giveAwayCurrentProductSelection || this.giveAwayCurrentVariantSelection.length === 0) return;

    // const max = this.getGiveAwayMaxCount();
    for (const sku of this.giveAwayCurrentVariantSelection) {
      // if (this.giveAwaySingleProdSelectionProds.length >= max) {
      //   this.alertService.alert(`Maximum ${max} product${max > 1 ? 's' : ''} allowed for this give away type`, 'error');
      //   break;
      // }
      const exists = this.giveAwaySingleProdSelectionProds.some(
        p => p.productSlug === this.giveAwayCurrentProductSelection.productSlug && p.variantSku === sku
      );
      if (!exists) {
        this.giveAwaySingleProdSelectionProds.push({
          productName: this.giveAwayCurrentProductSelection.productName,
          productSlug: this.giveAwayCurrentProductSelection.productSlug,
          variantSku: sku,
        });
      }
    }
    this.giveAwayCurrentProductSelection = undefined;
    this.giveAwayCurrentVariantSelection = [];
  }

  onRemoveGiveAwayProduct(index:number) {
    this.giveAwaySingleProdSelectionProds.splice(index,1);
  }

  isPopupProdSelectionType(): boolean {
    return /^POPUP_\d+_PROD_SELECTION$/.test(this.giveAwayType);
  }

  getGiveAwayMaxCount(): number {
    const match = this.giveAwayType.match(/^POPUP_(\d+)_PROD_SELECTION$/);
    return match ? parseInt(match[1], 10) : 1;
  }


}
